/**
 * Desktop top bar — page title + subtitle + search/notif actions.
 *
 * Pixel-faithful port of `WebTopbar` in `screens-web-shell.jsx`.
 */
import type { ReactNode } from 'react';

import Icon from '@/components/common/Icon';

export interface TopbarProps {
  title: string;
  subtitle?: string;
  onSearch?: () => void;
  onNotif?: () => void;
  hasUnreadNotif?: boolean;
  right?: ReactNode;
}

export default function Topbar({
  title,
  subtitle,
  onSearch,
  onNotif,
  hasUnreadNotif = false,
  right,
}: TopbarProps): JSX.Element {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '22px 32px 18px',
        borderBottom: '1px solid var(--border-1)',
        background: 'var(--bg-app)',
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}
    >
      <div>
        <h1
          style={{
            font: '600 22px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <div
            style={{
              font: '400 13px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 3,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {right}
        <button
          type="button"
          onClick={onSearch}
          aria-label="搜尋"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'transparent',
            border: '1px solid var(--border-1)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name="search" size={18} stroke="var(--fg-2)" />
        </button>
        <button
          type="button"
          onClick={onNotif}
          aria-label="通知"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'transparent',
            border: '1px solid var(--border-1)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          <Icon name="bell" size={18} stroke="var(--fg-2)" />
          {hasUnreadNotif && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 8,
                right: 9,
                width: 8,
                height: 8,
                borderRadius: 4,
                background: 'var(--accent)',
                border: '1.5px solid var(--bg-app)',
              }}
            />
          )}
        </button>
      </div>
    </header>
  );
}
